import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import BlockIcon from '@mui/icons-material/Block';
import { createUser, updateUser, deleteUser, getUsers } from '../api';
import { getCurrentUser } from '../auth';
import { UserForm } from './UserForm';
import { ClientStopSourcesDialog } from './ClientStopSourcesDialog';

export const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [users, setUsers] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState<any>(null);
  const [stopSourcesOpen, setStopSourcesOpen] = React.useState(false);
  const [stopSourcesClient, setStopSourcesClient] = React.useState<any>(null);
  const currentUser = getCurrentUser();
  const isAdmin = currentUser?.role === 'ADMIN';

  const fetchUsers = React.useCallback(() => {
    setLoading(true);
    getUsers()
      .then(data => { setUsers(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async (data: any) => {
    try {
      const result = await createUser(data);
      setDialogOpen(false);
      fetchUsers();
      return result;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const handleEdit = async (data: any) => {
    try {
      const result = await updateUser(editingUser.id, data);
      setDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
      return result;
    } catch (err: any) {
      setError(err.message);
      throw err;
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('confirm_delete') || 'Are you sure?')) {
      try {
        await deleteUser(id);
        fetchUsers();
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  return (
    <>
      {isAdmin && (
        <Box mb={2} display="flex" gap={2} alignItems="center">
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => setDialogOpen(true)}
            fullWidth={isMobile}
          >
            {t('create') || 'Create'}
          </Button>
        </Box>
      )}
      {loading ? (
        <Typography>{t('loading') || 'Loading...'}</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {users.map((user) => (
            <Card key={user.id}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                    {user.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {user.id}
                  </Typography>
                </Box>
                <Box sx={{ mb: 1 }}>
                  <Chip 
                    label={user.role} 
                    size="small" 
                    color={user.role === 'ADMIN' ? 'error' : 'primary'}
                    variant="outlined"
                  />
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Создан: {new Date(user.created_at).toLocaleString('ru-RU')}
                  </Typography>
                  {isAdmin && (
                    <Box>
                      <IconButton size="small" color="primary" onClick={() => { setEditingUser(user); setDialogOpen(true); }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(user.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>{t('email') || 'Email'}</TableCell>
                <TableCell>{t('role') || 'Role'}</TableCell>
                <TableCell>{t('created_at') || 'Created at'}</TableCell>
                {isAdmin && <TableCell>{t('actions') || 'Actions'}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.id}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.created_at}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <IconButton onClick={() => { setEditingUser(user); setDialogOpen(true); }}>
                        <EditIcon />
                      </IconButton>
                      {user.role === 'CLIENT' && (
                        <IconButton
                          title="Стоп-источники"
                          onClick={() => { setStopSourcesClient(user); setStopSourcesOpen(true); }}
                        >
                          <BlockIcon />
                        </IconButton>
                      )}
                      <IconButton onClick={() => handleDelete(user.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {isAdmin && (
        <UserForm
          open={dialogOpen}
          onClose={() => { setDialogOpen(false); setEditingUser(null); }}
          user={editingUser}
          onSubmit={editingUser ? handleEdit : handleCreate}
        />
      )}
      <ClientStopSourcesDialog
        open={stopSourcesOpen}
        clientId={stopSourcesClient?.id ?? null}
        clientName={stopSourcesClient?.operator_name || stopSourcesClient?.email}
        onClose={() => { setStopSourcesOpen(false); setStopSourcesClient(null); }}
      />
    </>
  );
}; 